import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Search, Tablet, Plus, Download, AlertCircle, Check, Clock, Zap, X, MoreVertical, Upload, FileSpreadsheet, ChevronLeft, ChevronRight, UserCog, GraduationCap } from "lucide-react"; // Added Search and other icons here
import styles from "./EventDetailsPage.module.css";
import Breadcrumbs from "../../../components/Breadcrumbs/Breadcrumbs";
import { useAuth } from "../../../contexts/AuthContext";

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const isUpdateMode = new URLSearchParams(window.location.search).get("mode") === "update";
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Big View States
  const [selectedBigImage, setSelectedBigImage] = useState(null);
  const [selectedBigPDF, setSelectedBigPDF] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

  // Breadcrumbs Logic
  const currentPath = ['Admin', 'Academics', 'Events', 'View'];

  // Attendance States
  const [sammelanEvents, setSammelanEvents] = useState([]);
  const [selectedEventTitle, setSelectedEventTitle] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  const [availableDivisions, setAvailableDivisions] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  const [selectedDivision, setSelectedDivision] = useState("");
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);

  // Student Table & Search
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [presentStudentIds, setPresentStudentIds] = useState([]);
  const [parentsAttended, setParentsAttended] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Multimedia
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [attendanceSaved, setAttendanceSaved] = useState(false);

  /* =========================================================
      Fetch Event Details
  ========================================================= */
  useEffect(() => {
    if (!eventId || eventId === "attendance" || eventId === "manage") {
      setLoading(false);
      return;
    }
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/events/${eventId}`);
        const eventData = res.data;

        // Map Photos
        eventData.event_photos = Array.isArray(eventData.photos)
          ? eventData.photos.map((p) => {
            if (!p.file_path) return null;
            const fileName = p.file_path.replace(/\\/g, "/").split("/").pop();
            return `${API_BASE_URL}/uploads/events/photos/${fileName}`;
          }).filter(Boolean) : [];

        // Map Reports (PDFs)
        // Map Reports (PDFs)
        eventData.event_report_files = Array.isArray(eventData.reports)
          ? eventData.reports
            .map((r) => {
              const rawPath = r.file_path || r.filePath || r.report_url || r.file_name || r;
              if (!rawPath || typeof rawPath !== "string") return null;

              const fileName = rawPath.replace(/\\/g, "/").split("/").pop();
              if (!fileName || fileName.trim() === "") return null;

              return `${API_BASE_URL}/uploads/events/reports/${fileName}`;
            })
            .filter(Boolean)
          : [];

        setEvent(eventData);
      } catch (err) {
        setError("Could not load event details.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, API_BASE_URL]);

  /* =========================================================
      Attendance Management Logic (Keep Same)
  ========================================================= */
  useEffect(() => {
    if (!isUpdateMode) return;
    axios.get(`${API_BASE_URL}/api/attendance/sammelan-list`).then((res) => setSammelanEvents(res.data?.data || []));
    axios.get(`${API_BASE_URL}/api/attendance/jurisdictions?type=state`).then((res) => setStates(res.data?.data || []));
  }, [isUpdateMode, API_BASE_URL]);

  useEffect(() => {
    if (selectedState) {
      axios.get(`${API_BASE_URL}/api/attendance/jurisdictions?type=division&stateName=${selectedState}`)
        .then((res) => {
          setAvailableDivisions(res.data?.data || []);
          setSelectedDivisions([]);
          setAvailableDistricts([]);
          setSelectedDistricts([]);
        });
    }
  }, [selectedState, API_BASE_URL]);

  useEffect(() => {
    if (selectedDivisions.length > 0) {
      const divisionNames = selectedDivisions.map(d => d.juris_name);

      axios.get(`${API_BASE_URL}/api/attendance/jurisdictions`, { params: { type: "district", divisionNames: divisionNames.join(',') } })
        .then((res) => {
          setAvailableDistricts(res.data?.data || []);
          setSelectedDistricts([]);
          setAvailableBlocks([]);
          setSelectedBlocks([]);
        });
    }
    else {
      setAvailableDistricts([]);
      setSelectedDistricts([]);
      setAvailableBlocks([]);
      setSelectedDistricts([]);
    }
  }, [selectedDivisions, API_BASE_URL]);

  useEffect(() => {
    if (selectedDistricts.length > 0) {
      const divisionNames = selectedDivisions.map(d => d.juris_name);
      const districtNames = selectedDistricts.map((d) => d.juris_name);
      axios.get(`${API_BASE_URL}/api/attendance/jurisdictions`, {
        params: { type: "block", stateName: selectedState, divisionNames: divisionNames.join(','), districtNames: districtNames.join(',') }
      })
        .then((res) => setAvailableBlocks(res.data?.data || []));
    } else {
      setAvailableBlocks([]);
      setSelectedBlocks([]);
    }
  }, [selectedDistricts, selectedState, selectedDivisions, API_BASE_URL]);

  const moveToSelected = (item, type) => {
    if (type === "division") {
      setAvailableDivisions(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setSelectedDivisions(prev => [...prev, item]);
    }
    else if (type === "district") {
      setAvailableDistricts(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setSelectedDistricts(prev => [...prev, item]);
    } else {
      setAvailableBlocks(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setSelectedBlocks(prev => [...prev, item]);
    }
  };

  const moveToAvailable = (item, type) => {
    if (type === "division") {
      setSelectedDivisions(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setAvailableDivisions(prev => [...prev, item]);
    }
    else if (type === "district") {
      setSelectedDistricts(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setAvailableDistricts(prev => [...prev, item]);
    } else {
      setSelectedBlocks(prev => prev.filter(i => i.juris_code !== item.juris_code));
      setAvailableBlocks(prev => [...prev, item]);
    }
  };

  const handleMoveAll = (type, action) => {
    if (type === "division") {
      if (action === "select") {
        setSelectedDivisions(prev => [...prev, ...availableDivisions]);
        setAvailableDivisions([]);
      } else {
        setAvailableDivisions(prev => [...prev, ...selectedDivisions]);
        setSelectedDivisions([]);
      }
    } else if (type === "district") {
      if (action === "select") {
        setSelectedDistricts(prev => [...prev, ...availableDistricts]);
        setAvailableDistricts([]);
      } else {
        setAvailableDistricts(prev => [...prev, ...selectedDistricts]);
        setSelectedDistricts([]);
      }
    } else if (type === "block") {
      if (action === "select") {
        setSelectedBlocks(prev => [...prev, ...availableBlocks]);
        setAvailableBlocks([]);
      } else {
        setAvailableBlocks(prev => [...prev, ...selectedBlocks]);
        setSelectedBlocks([]);
      }
    }
  };

  const getActiveNames = (selected, available) => {
    if (selected.length > 0) return selected.map(x => x.juris_name);
    if (available.length > 0) return available.map(x => x.juris_name);
    return null;
  };

  const handleSearchStudents = async (pageNumber = 1) => {
    if (!selectedEventTitle) return alert("Please select a Sammelan Event.");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/attendance/students-list`, {
        eventTitle: selectedEventTitle,
        stateName: selectedState || null,
        districtNames: getActiveNames(selectedDistricts, availableDistricts),
        blockNames: getActiveNames(selectedBlocks, availableBlocks),
        page: pageNumber,
      });
      const data = res.data?.data || [];
      setStudents(data);
      setCurrentPage(pageNumber);
      setHasNextPage(data.length === 15);

      // Preserve locally checked items, and add newly fetched database-marked items
      const markedIds = data.filter(s => s.is_marked).map(s => s.student_id);
      setPresentStudentIds(prev => Array.from(new Set([...prev, ...markedIds])));

    } catch (err) {
      alert("Failed to load students.");
    }
  };



  const filteredStudents = students.filter(s =>
    s.student_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckAll = () => {
    const visibleIds = filteredStudents.map(s => s.student_id);
    setPresentStudentIds(prev => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleUncheckAll = () => {
    const visibleIds = filteredStudents.map(s => s.student_id);
    setPresentStudentIds(prev => prev.filter(id => !visibleIds.includes(id)));
  };

  const handleSaveAttendance = async (isDraft = false) => {
    const actualId =
      eventId === "attendance" || !eventId
        ? sammelanEvents.find((e) => e.event_title === selectedEventTitle)?.event_id
        : eventId;

    if (!actualId) return alert("Select an event first.");

    if (!isDraft && !attendanceSaved) {
      if (window.confirm(`Confirm attendance for ${presentStudentIds.length} students?`)) {
        setAttendanceSaved(true);
      }
      return;
    }

    const formData = new FormData();
    formData.append("eventId", String(actualId));
    formData.append("eventTitle", selectedEventTitle || "");
    formData.append("studentIds", JSON.stringify(presentStudentIds));
    formData.append("parents_attended", String(parentsAttended || 0));

    if (user?.user_id) formData.append("user_id", String(user.user_id));

    if (!isDraft) {
      if (selectedPhotos && selectedPhotos.length > 0) {
        selectedPhotos.forEach((file) => formData.append("photos", file));
      }
      if (selectedReports && selectedReports.length > 0) {
        selectedReports.forEach((file) => formData.append("reports", file));
      }
    }

    const token = localStorage.getItem("token") || localStorage.getItem("jwt") || localStorage.getItem("authToken");

    try {
      setIsSaving(true);
      await axios.post(`${API_BASE_URL}/api/attendance/save`, formData, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });

      if (isDraft) {
        alert("Draft saved successfully!");
      } else {
        alert("Attendance saved and counts updated successfully!");
        setAttendanceSaved(false);
        setStudents([]);
        setPresentStudentIds([]);
      }
    } catch (err) {
      console.error("Save Attendance Error:", err.response?.data || err);
      alert("Error saving: " + (err.response?.data?.msg || err.response?.data?.message || "Server Error (400)"));
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  };


  const renderDualBox = (title, available, selected, type) => (
    <div className={styles.dualBoxContainer} style={{ marginTop: "20px" }}>

      <div className={styles.box} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: '10px', marginBottom: '5px', borderBottom: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: 0 }}>Available {title}</h4>
          <button type="button" className={styles.bulkBtn} onClick={() => handleMoveAll(type, 'select')} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
            Check All
          </button>
        </div>

        <ul style={{ flex: 1, overflowY: 'auto', margin: 0, paddingRight: '5px' }}>
          {available.map((item) => (
            <li key={item.juris_code} onClick={() => moveToSelected(item, type)}>{item.juris_name} +</li>
          ))}
        </ul>
      </div>

      <div className={styles.box} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: '10px', marginBottom: '5px', borderBottom: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: 0 }}>Selected {title}</h4>
          <button type="button" className={styles.bulkBtn} onClick={() => handleMoveAll(type, 'clear')} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
            Clear All
          </button>
        </div>

        <ul style={{ flex: 1, overflowY: 'auto', margin: 0, paddingRight: '5px' }}>
          {selected.map((item) => (
            <li key={item.juris_code} onClick={() => moveToAvailable(item, type)}>{item.juris_name} ×</li>
          ))}
        </ul>
      </div>

    </div>
  );

  if (loading) return <div className={styles.loadingContainer}><div className={styles.spinner}></div></div>;

  return (
    <div className={styles.pageWrapper}>
      {/* BIG IMAGE OVERLAY */}
      {selectedBigImage && (
        <div className={styles.modalOverlay} onClick={() => setSelectedBigImage(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedBigImage(null)}>&times;</button>
            <img src={selectedBigImage} alt="Large View" className={styles.modalImg} />
          </div>
        </div>
      )}

      {/* BIG PDF OVERLAY */}
      {selectedBigPDF && (
        <div className={styles.modalOverlay} onClick={() => setSelectedBigPDF(null)}>
          <div
            className={styles.pdfModalContent}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "520px",
              width: "90%",
              background: "#ffffff",
              padding: "24px",
              borderRadius: "12px",
              textAlign: "center",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
              position: "relative"
            }}
          >
            {/* Header with high-visibility Close (X) icon */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "12px",
              marginBottom: "16px"
            }}>
              <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: "700", color: "#111827" }}>
                Event Report
              </h3>
              <button
                type="button"
                onClick={() => setSelectedBigPDF(null)}
                style={{
                  background: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#1f2937",
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  lineHeight: 1
                }}
                title="Close"
              >
                &times;
              </button>
            </div>

            {/* Content Body */}
            <div style={{ padding: "10px 0 20px 0" }}>
              <div style={{ fontSize: "3rem", marginBottom: "12px" }}>📄</div>
              <p style={{ fontWeight: "700", fontSize: "1rem", color: "#111827", wordBreak: "break-all", margin: "0 0 8px 0" }}>
                {selectedBigPDF.split("/").pop()}
              </p>
              <p style={{ fontSize: "0.85rem", color: "#4b5563", marginTop: "4px", marginBottom: "24px", lineHeight: 1.5 }}>
                Preview is blocked by browser security. Click below to view or download the report directly.
              </p>

              {/* Action Buttons with explicit dark text on the secondary button */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <a
                  href={selectedBigPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.primaryButton}
                  style={{
                    textDecoration: "none",
                    padding: "10px 20px",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Download size={16} /> Open / Download Report
                </a>
                <button
                  type="button"
                  onClick={() => setSelectedBigPDF(null)}
                  style={{
                    padding: "10px 22px",
                    borderRadius: "6px",
                    border: "1px solid #9ca3af",
                    background: "#f9fafb",
                    color: "#111827",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.container}>

        {/* ADD BREADCRUMBS HERE */}
        <Breadcrumbs path={currentPath} nonLinkSegments={['Admin', 'Academics', 'View']} />

        {isUpdateMode ? (
          /* ATTENDANCE SECTION */
          <div className={styles.attendanceSammelanSection}>
            <div className={styles.topBar}>
              <Link to="/admin/academics/events" className={styles.backLink}>← Back</Link>
              <h2 className={styles.sectionTitle} style={{ textAlign: "center", width: "100%" }}>
                Sammelan Attendance Tracking
              </h2>
            </div>

            <div className={styles.filterGrid}>
              <div className={styles.formGroup}>
                <label>1. Select Sammelan Event</label>
                <select value={selectedEventTitle} onChange={(e) => setSelectedEventTitle(e.target.value)}>
                  <option value="">-- Choose Event --</option>
                  {sammelanEvents.map((e) => <option key={e.event_id} value={e.event_title}>{e.event_title}</option>)}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>2. Select State</label>
                <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                  <option value="">-- State --</option>
                  {states.map((s) => <option key={s.juris_code} value={s.juris_name}>{s.juris_name}</option>)}
                </select>
              </div>
            </div>

            {selectedState && renderDualBox("Divisions", availableDivisions, selectedDivisions, "division")}
            {selectedDivisions.length > 0 && renderDualBox("Districts", availableDistricts, selectedDistricts, "district")}
            {selectedDistricts.length > 0 && renderDualBox("Blocks", availableBlocks, selectedBlocks, "block")}

            <button className={styles.primaryButton} onClick={() => handleSearchStudents(1)}>Search Students</button>

            {students.length > 0 && (
              <div className={styles.studentListSection}>
                <div className={styles.tableActionsBar}>
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      placeholder="🔍 Search student name "
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                  <div className={styles.bulkActions}>
                    <button onClick={handleCheckAll} className={styles.bulkBtn}>Check Visible</button>
                    <button onClick={handleUncheckAll} className={styles.bulkBtn}>Uncheck Visible</button>
                  </div>
                </div>

                <table className={styles.studentTable}>
                  <thead>
                    <tr>
                      <th>Mark</th>
                      <th>Student Name</th>
                      <th>Cohort</th>
                      <th>Batch</th>
                      <th>District</th>
                      <th>Block</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map((s) => (
                        <tr key={s.student_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={presentStudentIds.includes(s.student_id)}
                              onChange={(e) => {
                                if (e.target.checked) setPresentStudentIds(prev => [...prev, s.student_id]);
                                else setPresentStudentIds(prev => prev.filter(id => id !== s.student_id));
                              }}
                            />
                          </td>
                          <td><strong>{s.student_name}</strong></td>
                          <td><span className={styles.cohortBadge}>Cohort-{s.cohort_number || "—"}</span></td> {/* ✅ Render Cohort */}
                          <td>{s.batch_name || "—"}</td>                                                   {/* ✅ Render Batch */}
                          <td>{s.district_name}</td>
                          <td>{s.block_name}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No matching students found for "{searchTerm}"</td></tr>
                    )}
                  </tbody>
                </table>

                <div className={styles.paginationControls}>
                  <button className={styles.pageBtn} onClick={() => handleSearchStudents(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
                  <span className={styles.pageInfo}>Page {currentPage}</span>
                  <button className={styles.pageBtn} onClick={() => handleSearchStudents(currentPage + 1)} disabled={!hasNextPage}>Next</button>
                </div>

                <div className={styles.tableFooter}>
                  <span>Marked: <strong>{presentStudentIds.length}</strong> students</span>
                  {!attendanceSaved ? (
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <button className={styles.primaryButton} onClick={() => handleSaveAttendance(true)} disabled={isSaving}>
                        Save as Draft
                      </button>
                      <button className={styles.primaryButton} onClick={() => handleSaveAttendance(false)} disabled={isSaving}>
                        Confirm Student List
                      </button>
                    </div>
                  ) : (
                    <div className={styles.finalStepContainer}>
                      <div className={styles.formGroup} style={{ marginBottom: '20px' }}>
                        <label>How many Parents attended?</label>
                        <input type="number" value={parentsAttended} onChange={(e) => setParentsAttended(e.target.value)} className={styles.inputField} min="0" />
                      </div>
                      <div className={styles.uploadSection}>
                        <div className={styles.uploadCard}>
                          <h4>📸 Sammelan Photos (Max 4)</h4>
                          <input type="file" multiple accept="image/*" onChange={(e) => setSelectedPhotos(Array.from(e.target.files).slice(0, 4))} />
                        </div>
                        <div className={styles.uploadCard}>
                          <h4>📄 Event Report (PDF)</h4>
                          <input type="file" accept=".pdf" onChange={(e) => setSelectedReports(Array.from(e.target.files).slice(0, 1))} />
                        </div>
                      </div>
                      <div className={styles.actionRow}>
                        <button className={styles.saveBtn} onClick={handleSaveAttendance} disabled={isSaving}>
                          {isSaving ? "Saving..." : "Submit & Update Counts"}
                        </button>
                        <button className={styles.backLink} onClick={() => setAttendanceSaved(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', marginTop: '10px' }}>← Back to List</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* CLEAN VIEW MODE */
          <div className={styles.viewContent}>
            <div className={styles.topBar}>
              <Link to="/admin/academics/events" className={styles.backLink}>← Back</Link>
              <Link to={`/admin/academics/events/${eventId}/edit`} className={styles.editButton}>Edit Event</Link>
            </div>

            <div className={styles.hero}>
              <span className={styles.eventTypeBadge}>{event?.event_type_name}</span>
              <h1 className={styles.mainTitle}>{event?.event_title}</h1>
              <div className={styles.heroMetaGrid}>
                <div className={styles.metaItem}>📅 <strong>Start:</strong> {formatDate(event?.event_start_date)}</div>
                <div className={styles.metaItem}>⌛ <strong>End:</strong> {formatDate(event?.event_end_date)}</div>
                <div className={styles.metaItem}>📍 <strong>Location:</strong> {event?.event_location}</div>
              </div>
            </div>

            <div className={styles.detailsGrid}>
              <div className={styles.descriptionSection}>
                <h3>Description</h3>
                <p className={styles.descriptionText}>{event?.event_description || "No description provided."}</p>

                {/* CONDITIONAL PDF VIEW FOR SAMMELAN */}
                {event?.event_type_name === "Sammelan" && event?.event_report_files?.length > 0 && (
                  <div className={styles.reportViewSection}>
                    <hr className={styles.divider} />
                    <h3>📄 Event Report</h3>
                    <div className={styles.reportLinkWrapper} onClick={() => setSelectedBigPDF(event.event_report_files[0])}>
                      <span>Click to view report: {event.event_report_files[0].split('/').pop()}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.statsCard}>
                <h3>Attendance Summary</h3>
                <div className={styles.statRow}><span>Boys:</span> <strong>{event?.boys_attended || 0}</strong></div>
                <div className={styles.statRow}><span>Girls:</span> <strong>{event?.girls_attended || 0}</strong></div>
                <div className={styles.statRow}><span>Parents:</span> <strong>{event?.parents_attended || 0}</strong></div>
              </div>
            </div>

            <div className={styles.gallerySection}>
              <h3>📸 Gallery</h3>
              <div className={styles.gallerySingleRow}>
                {event?.event_photos?.length > 0 ? (
                  event.event_photos.map((photo, i) => (
                    <div key={i} className={styles.galleryImgWrapper} onClick={() => setSelectedBigImage(photo)}>
                      <img src={photo} alt="event" className={styles.galleryImgUniform} />
                    </div>
                  ))
                ) : (
                  <p className={styles.emptyGallery}>No photos available for this event.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailsPage;